<template>
    <div
        class="d-flex align-items-center flex-md-grow-1"
        id="session-container"
        :class="{ 'cursor-resize': isResizingColumn, 'case-session': !editMode, 'case-session-edit-mode': editMode }"
    >
        <div :key="componentKey" class="node-container container-fluid">
            <div
                id="node-content-container"
                class="node-holder d-flex flex-column flex-session-row justify-content-center px-1 px-sm-3 px-2xl-0"
            >
                <!-- Empty Screen Placeholder -->
                <div v-show="editingEnabled && !currentNodeHasColumns && !loading" class="px-4 mw-100">
                    <div
                        role="button"
                        class="empty-screen-display rounded-lg d-flex justify-content-center align-items-center mw-100"
                        @click="openCreatePanelAtPosition(0, 'start')"
                    >
                        <div>
                            <p class="text-center mb-1">
                                <span aria-hidden="true" class="icon icon-glyphicons-basic-plus"></span>
                            </p>
                            <p class="text-center mb-0">Press to add content</p>
                        </div>
                    </div>
                </div>

                <!-- Create Panel Left Button -->
                <div
                    class="create-column-container"
                    v-show="showPanelCreatorSideBtns"
                    :class="{ 'd-flex': showPanelCreatorSideBtns }"
                >
                    <div
                        class="create-column justify-content-center align-items-center d-flex"
                        :class="{
                            'create-column-enabled': !isResizingColumn,
                            'create-column-show': createAtPosition === 'start' && createAtColumnIndex === -1,
                        }"
                        role="button"
                        @click="openCreatePanelAtPosition(-1, 'start')"
                    >
                        <span class="icon icon-glyphicons-basic-plus"></span>
                    </div>
                </div>

                <!-- Drop-Create Column Left - Drop Area -->
                <draggable
                    v-if="editingEnabled"
                    v-show="showColumnCreateDropAreas"
                    class="panel-column-editable panel-column-create mr-session-1 mr-xxl-2"
                    :class="{
                        'panel-column-visible d-flex flex-column': showColumnCreateDropAreas,
                        'panel-column-hide': !isDragging,
                    }"
                    :list="createColumnDroppedPanels"
                    :style="{ 'max-width': createColumnWidth + 'px' }"
                    group="columns"
                    @end="endPanelDrag()"
                    @add="dropToCreateColumn('left')"
                ></draggable>

                <!-- Resizable Panel Column -->
                <div
                    v-for="(column, index) in currentNode.columns"
                    :key="column.uid"
                    class="d-flex panel-column"
                    :class="{
                        'position-relative': editMode,
                        'panel-column-smooth': isResizingColumn,
                        'panel-column-visible': isDragging || resizingColumnIndex === index,
                        'panel-column-drag-active panel-column-editable': isDragging,
                    }"
                    :style="{ width: column.width + 'px' }"
                    :data-col-index="index"
                >
                    <!-- Left Resize Handle -->
                    <div
                        v-if="editingEnabled"
                        class="resize-handle-container resize-handle-container-left"
                        :class="{
                            'resize-handle-show': resizingColumnIndex === index,
                            'resize-handle-hide':
                                (isResizingColumn && resizingColumnIndex !== index) || hideColumnResizeHandles,
                        }"
                    >
                        <div class="resize-handle"></div>
                    </div>

                    <!-- Draggable Panel List -->
                    <div class="d-flex flex-grow-1 flex-column mw-100 panel-column-content">
                        <draggable
                            v-bind="draggablePanelOptions"
                            :list="column.panels"
                            group="columns"
                            class="rounded-lg d-flex flex-grow-1 flex-column mw-100"
                            :disabled="disablePanelDragging"
                            @start="startPanelDrag(column)"
                            @end="endPanelDrag()"
                            @change="layoutChangedCb($event)"
                            @choose="choosePanelForDrag($event)"
                            @unchoose="unchoosePanelForDrag($event)"
                            filter=".prevent-draggable-drag"
                        >
                            <!-- Column Width Display Popover (Shown During Resize) -->
                            <div
                                v-if="editMode"
                                slot="header"
                                class="shadow-sm rounded draggable-ignore column-width-popover bg-dark no-select"
                                :class="{
                                    'text-warning': resizingColumnMaxWidthReached || resizingColumnMinWidthReached,
                                }"
                                style="opacity: 1"
                                v-show="isResizingColumn && resizingColumnIndex == index"
                            >
                                <div>
                                    <span class="icon icon-glyphicons-basic-ruler align-middle mr-1"></span>
                                    <span class="align-middle">{{ activeResizeWidth }}</span>
                                    <span class="tiny ml-tiny" v-show="resizingColumnMaxWidthReached">(max)</span>
                                    <span class="tiny ml-tiny" v-show="resizingColumnMinWidthReached">(min)</span>
                                </div>
                            </div>

                            <!-- Create Panel at Top of Column - Header Button -->
                            <div
                                v-if="editingEnabled"
                                role="button"
                                slot="header"
                                class="d-flex justify-content-center align-items-center add-panel-inline"
                                :class="{
                                    'add-panel-inline-show':
                                        createAtColumnIndex === index && createAtPosition == 'start',
                                    'add-panel-inline-hide': isDragging || isResizingColumn,
                                }"
                                @click="openCreatePanelAtPosition(index, 'start')"
                            >
                                <span class="icon icon-glyphicons-basic-plus"></span>
                            </div>

                            <!-- Panel List -->
                            <div
                                v-for="(panel, panelIndex) in column.panels"
                                :key="panel.id"
                                class="d-flex flex-column rounded position-relative mw-100 panel"
                                :class="{
                                    'panel-draggable': editMode && !disablePanelDragging && !isResizingColumn,
                                    'flex-grow-1': panel.vertical_flex_display === 'grow',
                                    'flex-grow-0': panel.vertical_flex_display === 'shrink',
                                    'panel-settle-animate': !preventAnimations,
                                }"
                                :data-panel-id="panel.id"
                                :style="{
                                    width: panel.isChosenForDrag ? panel.tempDragWidth + 'px' : 'auto',
                                    'animation-delay': `${index * 0.05 + panelIndex * 0.05}s`,
                                }"
                            >
                                <!-- Hoverable Panel Edit Toolbar -->
                                <div
                                    v-if="editMode && !fullScreenIsActive"
                                    class="shadow panel-toolbar rounded"
                                    :class="{ 'panel-toolbar-hide': isDragging || isResizingColumn }"
                                    v-show="!imageEditModeIsActive"
                                >
                                    <div class="btn-group" role="group" aria-label="Content action toolbar">
                                        <button
                                            role="button"
                                            class="btn btn-dark draggable-ignore toolbar-popover"
                                            aria-label="Edit content"
                                            data-content="Edit"
                                            @click="openEditPanel(panel)"
                                        >
                                            <span class="icon icon-lg icon-glyphicons-basic-pencil align-middle"></span>
                                        </button>
                                        <button
                                            role="button"
                                            class="btn btn-dark draggable-ignore toolbar-popover"
                                            aria-label="Clone content"
                                            data-content="Clone"
                                            @click="openClonePanel(panel)"
                                        >
                                            <span
                                                class="icon icon-lg icon-glyphicons-basic-squares align-middle"
                                            ></span>
                                        </button>
                                        <button
                                            role="button"
                                            class="btn btn-dark draggable-ignore toolbar-popover"
                                            aria-label="Delete content"
                                            data-content="Delete"
                                            @click="openDeletePanel(panel)"
                                        >
                                            <span class="icon icon-lg icon-glyphicons-basic-bin align-middle"></span>
                                        </button>
                                        <button
                                            v-show="column.panels.length > 1"
                                            role="button"
                                            class="btn btn-dark draggable-ignore toolbar-popover py-0"
                                            aria-label="Toggle Shrink/Expand"
                                            data-content="Toggle Shrink/Expand"
                                            @click="togglePanelVerticalFlex(panel, column)"
                                        >
                                            <img
                                                v-show="panel.vertical_flex_display === 'grow'"
                                                src="/static/general/img/icons/collapse.svg"
                                                width="17"
                                                class="align-middle"
                                            />
                                            <img
                                                v-show="panel.vertical_flex_display === 'shrink'"
                                                src="/static/general/img/icons/expand.svg"
                                                width="17"
                                                class="align-middle"
                                            />
                                        </button>
                                        <button
                                            v-show="panel.panel_type === 'custom'"
                                            role="button"
                                            class="btn btn-dark draggable-ignore toolbar-popover py-0"
                                            aria-label="Toggle Vertical Align"
                                            data-content="Toggle Vertical Align"
                                            @click="togglePanelVerticalContentAlign(panel)"
                                        >
                                            <span
                                                v-show="panel.vertical_align === 'center'"
                                                class="icon icon-xl icon-glyphicons-basic-align-top align-middle"
                                            ></span>
                                            <span
                                                v-show="panel.vertical_align === 'top'"
                                                class="icon icon-xl icon-glyphicons-basic-align-horizontal align-middle"
                                            ></span>
                                        </button>
                                        <div v-if="editingEnabled" class="btn bg-dark panel-drag-anchor">
                                            <img
                                                src="/static/general/img/icons/move.svg"
                                                height="18"
                                                class="align-middle"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <!-- Panel Content Display -->
                                <div
                                    class="d-flex flex-grow-1 global-theme"
                                    :class="{ 'disable-pointer no-select': isResizingColumn }"
                                >
                                    <!-- Buttons -->
                                    <buttons-panel
                                        v-if="panel.panel_type === 'multichoice'"
                                        :panel="panel"
                                        :node="currentNode"
                                        :sessionId="sessionId"
                                        :savedResponses="savedPanelResponses[panel.id].choice_responses"
                                        :is-previously-visited="nodeIsPreviouslyVisited"
                                        :correctChoicesCount.sync="panel.correctChoicesCount"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                        v-on:update-session="updateSession($event)"
                                        v-on:response-received="responsesReceivedCb()"
                                        v-on:trigger-vitals="triggerVitals($event)"
                                    ></buttons-panel>

                                    <!-- Multichoice & Multiselect -->
                                    <choice-panel
                                        v-else-if="
                                            panel.panel_type == 'singleselect' || panel.panel_type == 'multiselect'
                                        "
                                        :panel="panel"
                                        :sessionId="sessionId"
                                        :savedResponses="savedPanelResponses[panel.id].choice_responses"
                                        :choicesCount.sync="panel.choicesCount"
                                        :choiceResponses.sync="panel.choiceResponses"
                                        :correctChoicesCount.sync="panel.correctChoicesCount"
                                        :isSubmitted.sync="panel.isSubmitted"
                                        :locked="nodeIsPreviouslyVisited && !panel.allow_answer_changing"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                        v-on:response-received="responsesReceivedCb()"
                                        v-on:trigger-vitals="triggerVitals($event)"
                                    ></choice-panel>

                                    <!-- Text Response -->
                                    <text-response-panel
                                        v-else-if="panel.panel_type == 'textarea'"
                                        v-model="panel.textResponse"
                                        :sessionId="sessionId"
                                        :panel="panel"
                                        :savedResponses="savedPanelResponses[panel.id].text_responses"
                                        :locked="nodeIsPreviouslyVisited && !panel.allow_answer_changing"
                                        :isSubmitted.sync="panel.isSubmitted"
                                        :correctResponsesCount.sync="panel.correctResponsesCount"
                                        :isSavingResponse.sync="panel.isSavingResponse"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:submit="submitResponses()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                    ></text-response-panel>

                                    <!-- Display -->
                                    <custom-panel
                                        v-else-if="panel.panel_type === 'custom'"
                                        :node="currentNode"
                                        :panel="panel"
                                        :videoComplete.sync="panel.videoComplete"
                                        :editable="editMode"
                                        :ref="'panel-' + panel.id"
                                        :skip-media-to-end="nodeIsPreviouslyVisited"
                                        v-on:continue="continueToNextNode()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                    ></custom-panel>

                                    <!-- Notepad -->
                                    <notepad-panel
                                        v-else-if="panel.panel_type === 'notepad'"
                                        :sessionId="sessionId"
                                        :panel="panel"
                                        :edit-mode="editMode"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                    ></notepad-panel>

                                    <!-- Tabs -->
                                    <tabs-panel
                                        v-else-if="panel.panel_type == 'tabs'"
                                        :panel="panel"
                                        :editable="editMode"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                    ></tabs-panel>

                                    <!-- Voice Recording -->
                                    <voice-recording-panel
                                        v-else-if="panel.panel_type === 'voice_recording'"
                                        :panel="panel"
                                        :isSubmitted.sync="panel.isSubmitted"
                                        :locked="nodeIsPreviouslyVisited"
                                        :savedResponses="savedPanelResponses[panel.id].voice_responses"
                                        :voiceResponse.sync="panel.voiceResponse"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:submit="submitResponses()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                    ></voice-recording-panel>

                                    <!-- Order the Steps -->
                                    <step-order-panel
                                        v-else-if="panel.panel_type === 'step_order'"
                                        :panel="panel"
                                        :savedResponses="savedPanelResponses[panel.id].choice_responses"
                                        :orderedChoices.sync="panel.orderedChoices"
                                        :isEdited.sync="panel.isEdited"
                                        :correctChoicesCount.sync="panel.correctChoicesCount"
                                        :isSubmitted.sync="panel.isSubmitted"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                        v-on:submit="submitResponses()"
                                    ></step-order-panel>

                                    <!-- Categories -->
                                    <categories-panel
                                        v-else-if="panel.panel_type === 'categories'"
                                        :panel="panel"
                                        :categoryChoices.sync="panel.categoryChoices"
                                        :isEdited.sync="panel.isEdited"
                                        :correctChoicesCount.sync="panel.correctChoicesCount"
                                        :isSubmitted.sync="panel.isSubmitted"
                                        :savedResponses="savedPanelResponses[panel.id].choice_responses"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                        v-on:submit="submitResponses()"
                                    ></categories-panel>

                                    <!-- Custom Form -->
                                    <custom-form-panel
                                        v-else-if="panel.panel_type === 'custom_form'"
                                        :panel="panel"
                                        :locked="nodeIsPreviouslyVisited && !panel.allow_answer_changing"
                                        :savedResponses="savedPanelResponses[panel.id].choice_responses"
                                        :choiceResponses.sync="panel.choiceResponses"
                                        :correctChoicesCount.sync="panel.correctChoicesCount"
                                        :isSubmitted.sync="panel.isSubmitted"
                                        :isComplete.sync="panel.isComplete"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:edit-action="handleEditAction(panel, $event)"
                                        v-on:submit="submitResponses()"
                                    ></custom-form-panel>

                                    <!-- Image Hotspots -->
                                    <image-hotspots-panel
                                        v-else-if="panel.panel_type === 'image_hotspots'"
                                        :show-continue-button="currentNode.continue_button"
                                        :panel="panel"
                                        :sessionId="sessionId"
                                        :savedResponses="savedPanelResponses[panel.id].choice_responses"
                                        :correctChoicesCount.sync="panel.correctChoicesCount"
                                        :is-previously-visited="nodeIsPreviouslyVisited"
                                        :ref="'panel-' + panel.id"
                                        v-on:update-session="updateSession($event)"
                                        v-on:trigger-vitals="triggerVitals($event)"
                                        v-on:continue="continueToNextNode()"
                                        v-on:response-received="responsesReceivedCb()"
                                        v-on:edit="openEditPanel(panel)"
                                        v-on:update-field="updatePanelFields(panel, $event)"
                                    ></image-hotspots-panel>

                                    <!-- Vitals -->
                                    <vitals-panel
                                        v-else-if="panel.panel_type === 'vitals' && panel.vitals"
                                        :panel="panel"
                                        :ref="'panel-' + panel.id"
                                    ></vitals-panel>

                                    <!-- Linked Reponse -->
                                    <linked-response-panel
                                        v-else-if="panel.panel_type === 'linked_response'"
                                        :panel="panel"
                                        :session-id="sessionId"
                                        :ref="'panel-' + panel.id"
                                    ></linked-response-panel>

                                    <!-- Learner Results (Score) -->
                                    <score-panel
                                        v-else-if="panel.panel_type === 'score'"
                                        :panel="panel"
                                        :session="session"
                                        :ref="'panel-' + panel.id"
                                    ></score-panel>

                                    <!-- Chat Interaction Panel -->
                                    <chat-panel
                                        v-else-if="panel.panel_type === 'chat'"
                                        :panel="panel"
                                        :sessionId="sessionId"
                                        :correctResponsesCount.sync="panel.correctResponsesCount"
                                        :savedResponses="savedPanelResponses[panel.id].intents_discovered"
                                        :isSubmitted.sync="panel.isSubmitted"
                                        v-on:response-received="responsesReceivedCb()"
                                        :locked="nodeIsPreviouslyVisited && !panel.allow_answer_changing"
                                        :ref="'panel-' + panel.id"
                                    ></chat-panel>

                                    <!-- Chat Interaction Panel (Version 2) -->
                                    <custom-gpt-panel
                                        v-else-if="panel.panel_type === 'chat_v2'"
                                        :node="currentNode"
                                        :panel="panel"
                                        :sessionId="sessionId"
                                        :correctResponsesCount.sync="panel.correctResponsesCount"
                                        :savedResponses="savedPanelResponses[panel.id].chat_conversations"
                                        v-on:response-received="responsesReceivedCb()"
                                        :locked="nodeIsPreviouslyVisited && !panel.allow_answer_changing"
                                        :ref="'panel-' + panel.id"
                                    ></custom-gpt-panel>

                                    <!-- Discovery Panel -->
                                    <discovery-panel
                                        v-else-if="panel.panel_type === 'discovery'"
                                        :panel="panel"
                                        :correct-responses-current="correctResponseCount"
                                        :correct-responses-visible="currentNode.correct_responses_visible"
                                        :ref="'panel-' + panel.id"
                                    ></discovery-panel>

                                    <!-- 360 Video (Use Hotspots Panel) -->
                                    <image-hotspots-panel
                                        v-else-if="panel.panel_type === '3d_video'"
                                        :show-continue-button="currentNode.continue_button"
                                        :panel="panel"
                                        :sessionId="sessionId"
                                        :is-previously-visited="nodeIsPreviouslyVisited"
                                        :ref="'panel-' + panel.id"
                                        v-on:continue="continueToNextNode()"
                                        v-on:response-received="responsesReceivedCb()"
                                        v-on:edit="openEditPanel(panel)"
                                        v-on:update-field="updatePanelFields(panel, $event)"
                                        :videoComplete.sync="panel.videoComplete"
                                    ></image-hotspots-panel>
                                </div>
                            </div>

                            <!-- Create Panel at Bottom of Column - Footer Button -->
                            <div
                                v-if="editingEnabled"
                                role="button"
                                slot="footer"
                                class="d-flex justify-content-center align-items-center add-panel-inline"
                                :class="{
                                    'add-panel-inline-show': createAtColumnIndex === index && createAtPosition == 'end',
                                    'add-panel-inline-hide': isDragging || isResizingColumn,
                                }"
                                @click="openCreatePanelAtPosition(index, 'end')"
                            >
                                <span class="icon icon-glyphicons-basic-plus"></span>
                            </div>
                        </draggable>
                    </div>

                    <!-- Right Resize Handle -->
                    <div
                        v-if="editingEnabled"
                        class="resize-handle-container resize-handle-container-right"
                        :class="{
                            'resize-handle-show': resizingColumnIndex === index,
                            'resize-handle-hide':
                                (isResizingColumn && resizingColumnIndex !== index) || hideColumnResizeHandles,
                        }"
                    >
                        <div class="resize-handle"></div>
                    </div>
                </div>

                <!-- Drop-Create Column Right - Drop Area -->
                <draggable
                    v-if="editingEnabled"
                    v-show="showColumnCreateDropAreas"
                    class="panel-column-editable panel-column-create ml-session-1 ml-xxl-2"
                    :class="{
                        'panel-column-visible d-flex flex-column': showColumnCreateDropAreas,
                        'panel-column-hide': !isDragging,
                    }"
                    :list="createColumnDroppedPanels"
                    :style="{ 'max-width': createColumnWidth + 'px' }"
                    group="columns"
                    @end="endPanelDrag()"
                    @add="dropToCreateColumn('right')"
                ></draggable>

                <!-- Create Panel Right Button -->
                <div
                    class="create-column-container"
                    v-show="showPanelCreatorSideBtns"
                    :class="{ 'd-flex': showPanelCreatorSideBtns }"
                >
                    <div
                        class="create-column justify-content-center align-items-center d-flex"
                        :class="{
                            'create-column-enabled': !isResizingColumn,
                            'create-column-show':
                                createAtPosition === 'start' && createAtColumnIndex === currentNode.columns.length,
                        }"
                        role="button"
                        @click="openCreatePanelAtPosition(currentNode.columns.length, 'start')"
                    >
                        <span class="icon icon-glyphicons-basic-plus"></span>
                    </div>
                </div>
            </div>

            <!-- Bottom Nav Buttons -->
            <div>
                <div
                    class="col-12 d-flex align-items-center justify-content-center"
                    :class="{ 'mt-bottom-navs': !editMode, 'mt-3': editMode }"
                >
                    <div class="d-flex align-items-center global-theme mb-1">
                        <!-- Slot for Additional Actions -->
                        <slot name="actions"></slot>

                        <!-- Back Button -->
                        <session-button
                            v-if="showNavigation && navigationStyle == 'back_button' && !isViewingFirstNode"
                            @click="navigateBack()"
                            :disabled="loading"
                            class="btn-gray mx-1"
                        >
                            <span
                                aria-hidden="true"
                                class="icon icon-glyphicons-basic-chevron-left align-middle dropup-icon"
                            ></span>
                            <span class="ml-1">{{ labelsTranslated.back }}</span>
                        </session-button>

                        <!-- Case Navigation Menu -->
                        <div
                            v-else-if="showNavigation && navigationStyle == 'menu' && !isViewingFirstNode"
                            class="btn-group dropup mx-1"
                        >
                            <button
                                :disabled="navigationNodes.length < 2 || loading"
                                role="button"
                                class="btn btn-gray btn-session btn-session-bottom dropdown-toggle"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span class="mr-1">Navigation</span>
                                <span
                                    aria-hidden="true"
                                    class="icon icon-glyphicons-basic-chevron-up align-middle dropup-icon"
                                ></span>
                            </button>
                            <div class="dropdown-menu node-history-dropdown shadow-sm">
                                <a
                                    v-for="navNode in navigationNodes"
                                    :key="navNode.node.id"
                                    @click="navigateToHistoryNode(navNode)"
                                    class="dropdown-item node-history-item"
                                    :class="{ 'text-primary': navNode.node.id == currentNode.id }"
                                    href="#"
                                >
                                    {{ navNode.node.title }}
                                </a>
                            </div>
                        </div>

                        <!-- Continue/Submit Button -->
                        <session-footer-btns
                            v-show="!loading"
                            @continue="continueToNextNode()"
                            @submit="submitResponses()"
                        ></session-footer-btns>

                        <!-- Return Home Button -->
                        <session-button
                            v-if="atCompleteNode && isPageViewerSession"
                            @click="completeSession()"
                            :disabled="loading"
                            class="btn-brand"
                        >
                            {{ labelsTranslated.returnToPage }}
                        </session-button>

                        <!-- Restart Button -->
                        <session-button
                            v-else-if="atExitNode"
                            @click="restartSession()"
                            :disabled="loading"
                            class="btn-brand"
                        >
                            {{ labelsTranslated.returnToStart }}
                        </session-button>
                    </div>
                </div>
                <br class="d-sm-none" />
            </div>
        </div>

        <!-- Resume/Restart Modal -->
        <base-modal id="resume-restart-modal" :centered="true" class="global-theme">
            <template slot="title">
                <!-- <span class="icon icon-mr icon-glyphicons-basic-circle-empty-alert"></span> -->
                <span>{{ labelsTranslated.sessionInProgress }}</span>
            </template>
            <template>
                <p class="mb-3">
                    {{ labelsTranslated.resumeSessionMessage }}
                </p>
                <p class="small text-secondary mb-1">
                    {{ labelsTranslated.closeBrowserTabs }}
                </p>
            </template>
            <template slot="footer">
                <button
                    @click="restartSession()"
                    :disabled="loading"
                    class="btn btn-gray btn-session"
                    data-dismiss="modal"
                >
                    <span class="icon icon-mr icon-glyphicons-basic-restart"></span>
                    <span>{{ labelsTranslated.restart }}</span>
                </button>
                <button
                    @click="resumeSession()"
                    :disabled="loading"
                    class="btn btn-brand btn-session"
                    data-dismiss="modal"
                >
                    <!-- <span class="icon icon-mr icon-glyphicons-basic-play"></span> -->
                    <span>{{ labelsTranslated.resume }}</span>
                </button>
            </template>
        </base-modal>

        <!-- Global Case Styles -->
        <global-style-block v-if="caseSettings" :case-settings="caseSettings"></global-style-block>

        <!-- Pre-Loaded Files - Not Visible -->
        <div class="prefetch-element-container">
            <div v-for="element in prefetchedElementsStatic" :key="element.id">
                <div v-if="element.stored_file">
                    <img
                        v-if="element.element_type == 'image'"
                        :src="element.stored_file.signed_url"
                        aria-hidden="true"
                        alt=""
                        height="1"
                        width="1"
                    />
                    <audio
                        v-else-if="element.element_type == 'audio'"
                        :src="element.stored_file.signed_url"
                        aria-hidden="true"
                        style="opacity: 0"
                    ></audio>
                </div>
            </div>
            <div v-for="element in prefetchedVideoElements" :key="element.id">
                <video v-if="element.stored_file" style="width: 1px; height: 1px" aria-hidden="true">
                    <source :src="element.stored_file.signed_url" type="video/mp4" />
                </video>
            </div>
        </div>

        <browser-storage-permissions-modal
            ref="browserStoragePermissionsModal"
        ></browser-storage-permissions-modal>

        <!-- Panel Creator -->
        <panel-creator
            ref="panelCreator"
            v-on:created="panelCreatedCb($event)"
            v-on:cancelled="panelCreateCancelled()"
            :saved-node="currentNode"
        ></panel-creator>

        <!-- Panel Editor -->
        <panel-editor
            v-if="sessionCase"
            ref="panelEditor"
            v-on:updated="panelUpdatedCb()"
            v-on:deleted="panelDeletedCb()"
            :node="currentNode"
            :case-obj="sessionCase"
        ></panel-editor>
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseModal from "@/components/BaseModal"
import BrowserStoragePermissionsModal from "@/components/BrowserStoragePermissionsModal"
import VitalsPanel from "@/components/panels/VitalsPanel"
import CustomPanel from "@/components/panels/CustomPanel"
import ScorePanel from "@/components/panels/ScorePanel"
import LinkedResponsePanel from "@/components/panels/LinkedResponsePanel"
import ChatPanel from "@/components/panels/ChatPanel"
import CustomGptPanel from "@/components/panels/CustomGptPanel"
import ChoicePanel from "@/components/panels/ChoicePanel"
import TextResponsePanel from "@/components/panels/TextResponsePanel"
import StepOrderPanel from "@/components/panels/StepOrderPanel"
import ImageHotspotsPanel from "@/components/panels/ImageHotspotsPanel"
import ButtonsPanel from "@/components/panels/ButtonsPanel"
import GlobalStyleBlock from "@/components/GlobalStyleBlock"
import CategoriesPanel from "@/components/panels/CategoriesPanel"
import NotepadPanel from "@/components/panels/NotepadPanel"
import VoiceRecordingPanel from "@/components/panels/VoiceRecordingPanel"
import TabsPanel from "@/components/panels/TabsPanel"
import CustomFormPanel from "@/components/panels/CustomFormPanel"
import DiscoveryPanel from "@/components/panels/DiscoveryPanel"
import PanelCreator from "@/components/PanelCreator"
import PanelEditor from "@/components/PanelEditor"
import SessionButton from "@/components/SessionButton"
import SessionFooterBtns from "@/components/SessionFooterBtns"
import Utilities from "@/assets/js/utilities.js"
import draggable from "vuedraggable"
import interact from "interactjs"

// The maximum total width of all the columns on a given screen, in pixels.
// This limit is roughly equivalent to the widest a column can be made while
// using a 13inch laptop screen.
const maxTotalColumnWidth = 1350
// The minimum width of a single column.
const minColumnWidth = 300
// The increment used while resizing columns.
const panelGridSnappingSize = 50

export default {
    name: "CaseSession",
    props: {
        sessionId: {
            type: [String, Number],
            required: true,
        },
    },
    components: {
        BaseModal,
        BrowserStoragePermissionsModal,
        PanelCreator,
        PanelEditor,
        VitalsPanel,
        CustomPanel,
        ScorePanel,
        LinkedResponsePanel,
        ChatPanel,
        ChoicePanel,
        TextResponsePanel,
        StepOrderPanel,
        ImageHotspotsPanel,
        GlobalStyleBlock,
        CategoriesPanel,
        NotepadPanel,
        VoiceRecordingPanel,
        TabsPanel,
        CustomFormPanel,
        ButtonsPanel,
        DiscoveryPanel,
        CustomGptPanel,
        SessionButton,
        SessionFooterBtns,
        draggable,
    },
    data() {
        return {
            // This property is used to force the entire component to refresh by incrementing it.
            componentKey: 0,
            session: {
                current_node: {
                    columns: [],
                },
                navigation_nodes: [],
                active_timers: [],
                panel_responses: null,
                is_viewing_initial_node: false,
            },
            modalTitle: "",
            modalMessage: "",
            extraBottomMargin: false,
            prefetchedElements: [],
            continueIsLoading: false,
            submitIsLoading: false,
            navigationNodes: [],
            upcomingNodes: [],
            animationsComplete: false,
            draggablePanelOptions: {
                draggable: ".panel",
                filter: ".draggable-ignore",
            },
            resizableColumnInteract: null,
            // Set to true when a panel is being dragged.
            isDragging: false,
            // Set to true when a column is being resized.
            isResizingColumn: false,
            // The index of the column currently being resized. Set to null when
            // no column is being resized.
            resizingColumnIndex: null,
            // The width of the column that's actively being resized.
            activeResizeWidth: null,
            // A list used to capture columns being dragged into the left and right create-column
            // drop areas. This list is cleared out immediately after a panel is dropped into it.
            createColumnDroppedPanels: [],
            // Stores the width of the column the current panel being dragged was moved from.
            createColumnWidth: 150,
            // The index of the column in which to place a newly created panel.
            createAtColumnIndex: null,
            // The position within a row ("start" or "end") to place a newly created panel.
            createAtPosition: "start",
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        editMode() {
            return this.$store.getters.previewEditMode
        },
        readOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        editingEnabled() {
            return this.editMode && !this.readOnly
        },
        imageEditModeIsActive() {
            return this.$store.getters.imageEditModeIsActive
        },
        communityViewActive() {
            return this.$route.path.startsWith("/community")
        },
        isPageViewerSession() {
            return Boolean(this.session.publish_page)
        },
        shareLinkSessionActive() {
            return this.$route.name == "ShareLinkCaseSession"
        },
        isCommunitySession() {
            return this.session.is_community_session
        },
        userIsAnonymous() {
            return this.$store.getters.userIsAnonymous
        },
        userHasActiveMembership() {
            return Boolean(this.$store.getters.activeMembership)
        },
        caseSettings() {
            return this.$store.getters.sessionCase
        },
        // The case currently being viewed.
        sessionCase() {
            return this.$store.getters.sessionCase
        },
        // The current node being viewed.
        currentNode() {
            return this.session.current_node
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        continueButtonText() {
            return this.$store.getters.continueBtnText
        },
        // Prevents panel 'settle' animation. Don't animate panels if preload_files setting is turned off,
        // because the combination of loading files while animating creates jerkiness. Also disable
        // if full screen is active, or after animations have already completed once.
        preventAnimations() {
            return !this.sessionCase.preload_files || this.fullScreenIsActive || this.animationsComplete
        },
        // Learner responses previously saved on each panel of the current node.
        savedPanelResponses() {
            return this.session.panel_responses
        },
        // A flattened list of all the panels contained in the current screen.
        currentPanelsFlat() {
            if (!this.currentNode.columns) {
                return []
            }
            return this.currentNode.columns.reduce((acc, column) => acc.concat(column.panels), [])
        },
        // An object containing formatted responses, based on each panel type. Only includes
        // panel types that require explicit submission of responses (i.e. using the submit
        // button).
        currentResponses() {
            let textResponses = this.currentPanelsFlat
                .filter((p) => p.panel_type == "textarea")
                .map((p) => {
                    return {
                        panelID: p.id,
                        text: p.textResponse,
                    }
                })
            let stepOrderResponses = this.currentPanelsFlat
                .filter((p) => p.panel_type == "step_order" && p.orderedChoices)
                .map((p) => {
                    return {
                        panelID: p.id,
                        response: p.orderedChoices.map((c) => c.id),
                    }
                })
            let choiceCategoryResponses = this.currentPanelsFlat
                .filter((p) => p.panel_type == "categories" && p.categoryChoices)
                .map((p) => {
                    return {
                        panelID: p.id,
                        response: p.categoryChoices.map((c) => {
                            return {
                                choiceID: c.id,
                                categoryID: c.categoryId,
                                position_x: c.position_x,
                                position_y: c.position_y,
                            }
                        }),
                    }
                })
            let choiceResponses = this.currentPanelsFlat
                .filter((p) => ["multiselect", "singleselect"].includes(p.panel_type) && p.choiceResponses)
                .map((p) => {
                    return {
                        panelID: p.id,
                        response: p.choiceResponses.map((c) => {
                            return c.id
                        }),
                    }
                })
            let formChoiceResponses = this.currentPanelsFlat
                .filter((p) => ["custom_form"].includes(p.panel_type))
                .map((p) => {
                    return {
                        panelID: p.id,
                        response: p.choiceResponses,
                    }
                })
            let voiceResponses = this.currentPanelsFlat
                .filter((p) => ["voice_recording"].includes(p.panel_type) && p.voiceResponse && !p.isSubmitted)
                .map((p) => {
                    return {
                        panelID: p.id,
                        response: p.voiceResponse,
                    }
                })
            let responses = {
                textResponses: textResponses,
                stepOrderResponses: stepOrderResponses,
                choiceResponses: choiceResponses,
                choiceCategoryResponses: choiceCategoryResponses,
                formChoiceResponses: formChoiceResponses,
                voiceResponses: voiceResponses,
                notes: this.session.notes_content,
            }
            return responses
        },
        // Current count of correct responses.
        correctResponseCount() {
            let panelTypes = [
                "multiselect",
                "singleselect",
                "image_hotspots",
                "multichoice",
                "textarea",
                "step_order",
                "categories",
                "custom_form",
                "chat",
                "chat_v2",
            ]
            let panelsWithCorrectness = this.currentPanelsFlat.filter((p) => panelTypes.includes(p.panel_type))

            // Add up the total number of correct choices made across all panels.
            let correctResponses = panelsWithCorrectness.reduce((total, panel) => {
                return total + (panel.correctChoicesCount || 0) + (panel.correctResponsesCount || 0)
            }, 0)
            return correctResponses
        },
        // Returns true if enough correct responses have been provided on the node, based on its
        // configuration.
        enoughCorrectResponses() {
            // Determine if enough correct choices have been made, based on the requirements
            // of the node.
            if (this.currentNode.correct_answers_required == "one") {
                return this.correctResponseCount > 0
            } else if (this.currentNode.correct_answers_required == "all") {
                return this.correctResponseCount >= this.currentNode.correct_responses_visible
            } else if (this.currentNode.correct_answers_required == "custom") {
                return this.correctResponseCount >= this.currentNode.correct_answers_required_num
            }
            return true
        },
        // Indicates if at least one choice has been made on each choice-type panels.
        choiceResponsesComplete() {
            let choiceTypePanels = ["multiselect", "singleselect", "image_hotspots", "multichoice"]
            let choicePanels = this.currentPanelsFlat.filter((p) => choiceTypePanels.includes(p.panel_type))
            // Return true if there aren't any select type panels on the current node.
            if (choicePanels.length == 0) {
                return true
            }

            // Determine whether there are any unanswered choice panels that require a response.
            let panelsRequiringResponse = ["multiselect", "singleselect"]
            let incompletePanels = choicePanels.find(
                (p) => !p.choicesCount && panelsRequiringResponse.includes(p.panel_type)
            )
            if (incompletePanels) {
                return false
            }
            return true
        },
        // Indicates if at least one selection has been made for selection-type panels.
        selectionResponsesMade() {
            let incompleteSelectionPanels = this.currentPanelsFlat.find(
                (p) => !p.choicesCount && ["multiselect", "singleselect"].includes(p.panel_type)
            )
            if (incompleteSelectionPanels) {
                return false
            }
            return true
        },
        // Indicates if all required text responses have been completed.
        textResponsesComplete() {
            // Check for any blank & required text responses.
            let incompleteTextResponsePanel = this.currentPanelsFlat.find(
                (panel) => panel.panel_type === "textarea" && panel.textarea_required && !panel.textResponse
            )
            return !incompleteTextResponsePanel
        },
        // Indicates if initial ordering of steps have been updated.
        stepOrderResponsesComplete() {
            let stepOrderPanels = this.currentPanelsFlat.filter((p) => p.panel_type == "step_order")
            if (!stepOrderPanels.length) {
                return true
            }
            let incompletePanel = stepOrderPanels.find((p) => {
                return !p.isEdited || (!p.isSubmitted && p.show_correctness && p.show_feedback_before_continuing)
            })
            return !incompletePanel
        },
        customFormsComplete() {
            let customFormPanels = this.currentPanelsFlat.filter((p) => p.panel_type == "custom_form")
            if (!customFormPanels.length) {
                return true
            }
            let incompleteFormPanel = customFormPanels.find((p) => {
                return !p.isComplete && p.all_items_required
            })
            return !incompleteFormPanel
        },
        voiceRecordingResponsesComplete() {
            let voiceRecordingPanels = this.currentPanelsFlat.filter((p) => p.panel_type == "voice_recording")
            if (!voiceRecordingPanels.length) {
                return true
            }
            let incompletePanel = voiceRecordingPanels.find((p) => !(p.voiceResponse || p.isSubmitted))
            return !incompletePanel
        },
        requiredVideosFinished() {
            let unfinishedVideo = this.currentPanelsFlat.find(
                (panel) => ["custom", "3d_video"].includes(panel.panel_type) && !panel.videoComplete
            )
            return !unfinishedVideo
        },
        // Returns true if the user has interacted with panels that require input before being
        // allowed to move forward.
        hasCompletedReqPanels() {
            return (
                this.choiceResponsesComplete &&
                this.textResponsesComplete &&
                this.stepOrderResponsesComplete &&
                this.customFormsComplete &&
                this.voiceRecordingResponsesComplete
            )
        },
        // Returns true if a user's response is in the process of being saved for at least one
        // panel. Currently only used for text response panels, which save the user's response
        // as they type. This property is used to disable the continue/submit buttons until
        // saving is complete.
        panelSavingInProgress() {
            return Boolean(this.currentPanelsFlat.find((panel) => panel.isSavingResponse))
        },
        // Controls enabling/disabling of the continue buttons.
        continueBtnEnabled() {
            // Determine if there's a panel on this screen that allows answer changing.
            let panelWithAnswerChanging = Boolean(this.currentPanelsFlat.find((panel) => panel.allow_answer_changing))

            // Determine if there's a hotspots or buttons panel. Treat this as a special case
            // because this panel type allows learners to branch regardless of the correct answer
            // requirements on the screen. This is because I don't currently have a good way to
            // show that a hotspot is disabled without revealing that it's a branching choice.
            // Therefore, if there's an image hotspots panel on screen, don't respect response locking.
            let branchingPanel = Boolean(
                this.currentPanelsFlat.find((panel) => ["image_hotspots", "multichoice"].includes(panel.panel_type))
            )

            // TODO: May want to remove this logic - this is only really useful for videos, to prevent
            // learners from having to re-watch the video they're revisiting.
            // If responses are locked, enable the continue button regardless of answers.
            let overrideContinueLogic = this.nodeIsPreviouslyVisited && !branchingPanel && !panelWithAnswerChanging
            if (overrideContinueLogic) {
                return true
            }

            return (
                this.enoughCorrectResponses &&
                this.hasCompletedReqPanels &&
                this.requiredVideosFinished &&
                !this.panelSavingInProgress
            )
        },
        // A message that describes to the user why the continue button is disabled
        // on the current node. An empty string prevents the message popover from being shown.
        continueBtnDisabledExplanation() {
            if (this.continueBtnEnabled) {
                // Never show a message if the continue button is enabled.
                return ""
            } else if (!this.requiredVideosFinished) {
                return `💡 ${this.labelsTranslated.continueBtnExp.videos}`
            } else if (!this.hasCompletedReqPanels) {
                return `💡 ${this.labelsTranslated.continueBtnExp.activities}`
            } else if (!this.enoughCorrectResponses) {
                return `💡 ${this.labelsTranslated.continueBtnExp.unfinished}`
            }
            return ""
        },
        submitBtnDisabledExplanation() {
            if (this.submitIsEnabled) {
                // Never show a message if the submit button is enabled.
                return ""
            }
            return `💡 ${this.labelsTranslated.submitBtnDisabledExp}`
        },
        showContinueBtn() {
            return this.currentNode.continue_button && !this.atEndNode && !this.submitIsRequired
        },
        showSubmitBtn() {
            return this.submitIsRequired && !this.atEndNode
        },
        submitIsEnabled() {
            let uneditedStepOrderPanel = this.currentPanelsFlat.find(
                (panel) => panel.panel_type === "step_order" && !panel.isEdited
            )
            return (
                !uneditedStepOrderPanel &&
                this.customFormsComplete &&
                !this.submitIsLoading &&
                this.textResponsesComplete &&
                this.voiceRecordingResponsesComplete &&
                this.selectionResponsesMade &&
                !this.panelSavingInProgress
            )
        },
        // Controls the display of the case navigation dropdown.
        showNavigation() {
            return (
                this.sessionCase &&
                this.sessionCase.navigation_enabled &&
                this.currentNode.navigation_enabled &&
                !this.currentNode.end_node &&
                !this.currentNode.final_node
            )
        },
        navigationStyle() {
            return this.sessionCase ? this.sessionCase.navigation_style : null
        },
        // Returns true if the user is viewing the initial node in their session
        // (the initial node visited when starting their session).
        isViewingFirstNode() {
            return this.session.is_viewing_initial_node
        },
        // Whether or not to lock responses on the current node.
        nodeIsPreviouslyVisited() {
            return this.session.current_node_was_previously_visited
        },
        responsesSubmitted() {
            return this.session.current_node_is_submitted
        },
        atEndNode() {
            return this.$store.getters.atEndNode
        },
        atExitNode() {
            return this.currentNode.end_node && !this.currentNode.final_node
        },
        atCompleteNode() {
            return this.currentNode.final_node
        },
        submitIsRequired() {
            let submitTypePanels = this.currentPanelsFlat.find(
                (panel) =>
                    ["step_order", "categories", "custom_form"].includes(panel.panel_type) &&
                    !panel.isSubmitted &&
                    panel.show_feedback_before_continuing &&
                    panel.show_correctness
            )
            let choiceDelayedFeedbackPanels = this.currentPanelsFlat.find(
                (panel) =>
                    ["multiselect", "singleselect"].includes(panel.panel_type) &&
                    !panel.record_selections_instantly &&
                    !panel.isSubmitted &&
                    panel.show_feedback_before_continuing
            )
            let textResponseAnalysisPanels = this.currentPanelsFlat.find(
                (panel) =>
                    panel.panel_type == "textarea" &&
                    (panel.analyze_text_responses || panel.enable_ai_analysis) &&
                    !panel.isSubmitted
            )
            let chatPanelRequiringSubmit = this.currentPanelsFlat.find(
                (panel) => panel.panel_type == "chat" && panel.show_missed_items === true && !panel.isSubmitted
            )
            let voiceResponsePanels = this.currentPanelsFlat.find(
                (panel) => panel.panel_type === "voice_recording" && !panel.isSubmitted && panel.enable_ai_analysis
            )
            return (
                Boolean(submitTypePanels) ||
                Boolean(textResponseAnalysisPanels) ||
                Boolean(choiceDelayedFeedbackPanels) ||
                Boolean(chatPanelRequiringSubmit) ||
                Boolean(voiceResponsePanels)
            )
        },
        submitIsAnalyzing() {
            let aiPanel = this.currentPanelsFlat.find((panel) => panel.enable_ai_analysis)
            return aiPanel && this.submitIsLoading
        },
        // The text label of the submit button.
        submitButtonLabel() {
            let panelsRequiringSubmit = [
                "step_order",
                "categories",
                "textarea",
                "multiselect",
                "singleselect",
                "custom_form",
                "voice_recording",
            ]
            if (this.currentPanelsFlat.find((p) => panelsRequiringSubmit.includes(p.panel_type))) {
                return this.labelsTranslated.submit
            }
            return this.labelsTranslated.done
        },
        activeTimers() {
            return this.session.active_timers
        },
        videoIsPlaying() {
            return (
                this.currentPanelsFlat.filter((p) => {
                    let findPlayingVideos = (el) =>
                        ["video", "vimeo", "youtube"].includes(el.element_type) && !el.videoComplete
                    let fullScreenElement = Boolean(p.elements.find(findPlayingVideos) && p.panel_type === "custom")
                    let fullScreenPreContent = Boolean(
                        p.pre_content_videos.find(findPlayingVideos) && p.show_video_first
                    )
                    return fullScreenElement || fullScreenPreContent
                }).length > 0
            )
        },
        // A list of all panels that are currently presenting full screen content.
        fullScreenPanels() {
            return this.currentPanelsFlat.filter((p) => {
                let findFullScreenElements = (el) => el.display_full_screen
                let fullScreenPanel = Boolean(p.display_full_screen || p.elements.find(findFullScreenElements))
                let fullScreenPreContent = Boolean(
                    p.pre_content_videos.find(findFullScreenElements) && p.show_video_first
                )
                return fullScreenPanel || fullScreenPreContent
            })
        },
        // Returns true if at least one panel is presenting content in full screen.
        fullScreenIsActive() {
            return this.fullScreenPanels.length > 0
        },
        showPanelPlaceholder() {
            return this.editMode && this.currentPanelsFlat.length === 0 && !this.loading
        },
        disablePanelDragging() {
            return this.imageEditModeIsActive || !this.editMode || this.readOnly || this.fullScreenIsActive
        },
        // Pre-loaded static file elements.
        prefetchedElementsStatic() {
            return this.prefetchedElements.filter((el) => ["image", "audio"].includes(el.element_type))
        },
        // Pre-loaded video-type elements.
        prefetchedVideoElements() {
            // Extract only a few of the preloaded video elements to show. If we load them all, we
            // could end up pulling GBs worth of data for branched nodes that may not be visited.
            return this.prefetchedElements.filter((el) => ["video", "vr"].includes(el.element_type)).slice(0, 2)
        },
        // When true, column resize handles are hidden.
        hideColumnResizeHandles() {
            return this.isDragging || this.imageEditModeIsActive
        },
        // Returns true if the current node contains at least one column.
        currentNodeHasColumns() {
            return this.currentNode.columns.length > 0
        },
        // Controls whether the left and right create buttons are shown.
        showPanelCreatorSideBtns() {
            return (
                this.editMode &&
                !this.isDragging &&
                !this.loading &&
                this.currentNodeHasColumns &&
                !this.readOnly &&
                // Only show side-create buttons if there's lots of horizontal column
                // width remaining. This is to discourage users from creating overly
                // horizontal layouts.
                this.availableColumnWidth > 400
            )
        },
        // When true, drop areas are shown beside existing columns while dragging a panel.
        showColumnCreateDropAreas() {
            return !this.maxContentWidthReached && this.isDragging
        },
        // The total available width left for columns to occupy on the node.
        availableColumnWidth() {
            let totalContentWidth = this.currentNode.columns.reduce(
                (totalWidth, column) => totalWidth + column.width,
                0
            )
            return maxTotalColumnWidth - totalContentWidth
        },
        // Returns true when the total column width has reached the maximum.
        maxContentWidthReached() {
            return this.availableColumnWidth <= 0
        },
        // The maximum allowable width for the column currently being resized.
        maxWidthForResizingColumn() {
            let vm = this
            // Get the total width of columns other than the one being resized.
            let otherColumnsTotalWidth = this.currentNode.columns
                .filter((col, index) => index !== vm.resizingColumnIndex)
                .reduce((totalWidth, column) => totalWidth + column.width, 0)
            return maxTotalColumnWidth - otherColumnsTotalWidth
        },
        // Returns true if the column being resized has reached its maximum allowable width.
        resizingColumnMaxWidthReached() {
            return this.activeResizeWidth >= this.maxWidthForResizingColumn
        },
        // Returns true if the column being resized is at its minimum width.
        resizingColumnMinWidthReached() {
            return this.activeResizeWidth <= minColumnWidth && !this.resizingColumnMaxWidthReached
        },
    },
    methods: {
        // Update the current session and all its data.
        async updateSession(newSession) {
            if (newSession.case.preload_nodes) {
                // Screen pre-loading is turned on, so check to see if the next node exists
                // in the list of pre-loaded nodes.
                let currentNode = this.upcomingNodes.find((n) => n.id == newSession.current_node.id)
                if (!currentNode) {
                    // The node wasn't preloaded, so fetch it from the server. This could happen because
                    // the learner has just started the session, or if they just refreshed their window.
                    currentNode = await this.fetchCurrentNode()
                }
                this.session = {
                    ...newSession,
                    current_node: currentNode,
                }
            } else {
                // No preloading is enabled, so just fully replace the session object.
                this.session = newSession
            }
            this.$store.dispatch("setSession", this.session)
            // Scroll to the top of the window.
            window.scrollTo(0, 0)
            return new Promise((resolve) => {
                resolve()
            })
        },
        // Continue a learner's session within a case.
        continueSession(sessionId = null, forceComponentRefresh = false) {
            let vm = this
            vm.$store.dispatch("addLoad")
            // If no session ID was passed, default to using the current session ID on the component prop.
            if (!sessionId) {
                sessionId = this.sessionId
            }
            return new Promise((resolve, reject) => {
                http.post(`/api/students/sessions/${sessionId}/`)
                    .then(async function (response) {
                        await vm.updateSession(response.data)

                        if (forceComponentRefresh) {
                            vm.refreshComponent()
                        }

                        vm.initWindowTitle()
                        resolve(response)
                    })
                    .catch(function (error) {
                        reject(error)
                    })
                    .finally(() => {
                        vm.$store.dispatch("removeLoad")
                    })
            })
        },
        // Fetch details about the current node being viewed in the session.
        fetchCurrentNode() {
            return new Promise((resolve) => {
                http.get(`/api/students/sessions/${this.sessionId}/current-node/`).then(function (response) {
                    resolve(response.data)
                })
            })
        },
        // Force a refresh of the entire component by increasing the 'key' by one. This
        // forces all created/mounted methods to be ran again.
        refreshComponent() {
            this.componentKey += 1
            this.initEditBtnPopovers()
        },
        // Used during session preview edit mode - refreshes data for the current session.
        refreshSession() {
            this.$store.dispatch("addLoad")
            let vm = this
            return new Promise((resolve) => {
                vm.continueSession().then(() => {
                    vm.$nextTick(() => vm.refreshComponent())
                    vm.$store.dispatch("removeLoad")
                    resolve()
                })
            })
        },
        // Fetch data about the current session.
        fetchSession() {
            this.$store.dispatch("addLoad")
            let vm = this
            http.get(`/api/sessions/${this.sessionId}/details/`)
                .then(function (response) {
                    vm.session = response.data
                })
                .finally(() => vm.$store.dispatch("removeLoad"))
        },
        // Initialize the videoComplete attribute on custom panels - this attribute is used to
        // track whether the video has completed or not.
        initializeCustomPanels() {
            let customPanels = this.currentPanelsFlat.filter((p) => p.panel_type == "custom")
            let vm = this
            // Explicitly setting this attribute fixes a bug causing the videoComplete attribute
            // to be undefined on the panel object when editing through preview edit mode.
            customPanels.forEach((panel) => {
                // Set to true initially for the custom panel component to overwrite if media
                // is present.
                vm.$set(panel, "videoComplete", true)
            })
        },
        // Fetch stored files associated with upcoming nodes, so that the browser caches them
        // (for a smoother learner experience).
        prefetchElements() {
            let vm = this
            http.get(`/api/sessions/${this.session.id}/prefetch-elements/`).then(function (response) {
                vm.prefetchedElements = response.data
            })
        },
        // Fetch upcoming nodes, based on the learner's current position.
        prefetchNodes() {
            let vm = this
            http.get(`/api/sessions/${this.session.id}/prefetch-nodes/`).then(function (response) {
                vm.upcomingNodes = response.data
            })
        },
        // Navigate to a history node.
        navigateToHistoryNode(historyNode) {
            if (historyNode.node.id == this.currentNode.id) {
                return
            }
            this.$store.dispatch("addLoad")
            let vm = this
            http.post(`/api/students/sessions/${this.sessionId}/navigate/${historyNode.id}/`).then(function (response) {
                vm.updateSession(response.data).then(() => {
                    vm.$store.dispatch("removeLoad")
                })
            })
        },
        // Navigate to the previously visited node.
        navigateBack() {
            this.$store.dispatch("addLoad")
            let vm = this
            http.post(`/api/students/sessions/${this.sessionId}/navigate/back/`).then(function (response) {
                vm.updateSession(response.data).finally(() => vm.$store.dispatch("removeLoad"))
            })
        },
        updateNavigationNodeList() {
            let vm = this
            http.get(`/api/students/sessions/${this.sessionId}/navigate/`).then(function (response) {
                vm.navigationNodes = response.data
            })
        },
        // End the current session.
        endSession() {
            let vm = this
            vm.$store.dispatch("addLoad")
            return new Promise((resolve) => {
                http.post(`/api/students/sessions/${this.sessionId}/end/`).finally(function () {
                    vm.$store.dispatch("removeLoad")
                    resolve()
                })
            })
        },
        // Complete the session and return to the case library.
        async completeSession() {
            await this.endSession()
            if (this.isPageViewerSession) {
                // Navigate to the page view.
                this.$emit("return-to-page")
            } else if (this.communityViewActive || !this.sessionCase.organization || !this.userHasActiveMembership) {
                // Navigate to the community library.
                this.$router.push({ name: "CommunityCaseDetail", params: { id: this.sessionCase.id } })
            } else {
                // Navigate to case library.
                this.$router.push({ name: "CaseLibrary" })
            }
        },
        // Start a new case session.
        async restartSession() {
            $("#resume-restart-modal").modal("hide")
            this.$store.dispatch("addLoad")
            await this.endSession()
            let vm = this
            if (this.$route.name == "PreviewCaseSession") {
                this.restartPreviewSession().finally(() => vm.refreshComponent())
            } else {
                this.restartLearnerSession().finally(() => vm.refreshComponent())
            }
        },
        // Restart a case preview session.
        restartPreviewSession(startAtCurrentNode = false) {
            let previewSessionStartURL = "/api/cases/" + this.sessionCase.id + "/preview/start/"
            let vm = this
            return http
                .post(previewSessionStartURL, {
                    startNodeID: startAtCurrentNode ? this.currentNode.id : null,
                })
                .then(function (response) {
                    // Start a regular preview session.
                    vm.$router.push({
                        name: "PreviewCaseSession",
                        params: {
                            caseID: String(vm.sessionCase.id),
                            sessionID: String(response.data.session_id),
                        },
                    })
                    vm.continueSession(response.data.session_id, true)
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        // Restart a learner case session.
        restartLearnerSession() {
            let vm = this
            return http
                .post("/api/students/sessions/" + this.sessionId + "/restart/")
                .then(function (response) {
                    if (vm.isPageViewerSession) {
                        // Route to the page session view.
                        vm.$router.push({
                            // Use the current route name as there are two page viewer route variations.
                            name: vm.$route.name,
                            params: {
                                id: vm.$route.params.pageID,
                                shareLinkID: vm.$route.params.shareLinkID,
                                sessionID: response.data.session_id,
                            },
                        })
                    } else if (vm.communityViewActive) {
                        // Route to community session view.
                        vm.$router.push({
                            name: "CommunityCaseSession",
                            params: {
                                caseID: vm.sessionCase.id,
                                sessionID: response.data.session_id,
                            },
                        })
                    } else if (vm.shareLinkSessionActive) {
                        // Route to share link session view.
                        vm.$router.push({
                            name: "ShareLinkCaseSession",
                            params: {
                                shareLinkID: vm.$route.params.shareLinkID,
                                sessionID: response.data.session_id,
                            },
                        })
                    } else {
                        // Route to org member session view.
                        vm.$router.push({
                            name: "LearnerCaseSession",
                            params: {
                                sessionID: response.data.session_id,
                            },
                        })
                    }
                    vm.continueSession(response.data.session_id)
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        resetPreviewSession() {
            this.$store.dispatch("addLoad")
            this.restartPreviewSession(true)
        },
        // Continue to the next node.
        continueToNextNode() {
            this.continueIsLoading = true
            let vm = this
            http.post(`/api/students/sessions/${this.sessionId}/continue/`, {
                responses: this.currentResponses,
            }).then(function (response) {
                vm.updateSession(response.data).finally(() => {
                    vm.continueIsLoading = false
                    // Unfocus the continue button.
                    let continueBtn = vm.$refs.continueBtn
                    if (continueBtn) {
                        continueBtn.unfocus()
                    }
                })
            })
        },
        submitResponses() {
            this.submitIsLoading = true
            let vm = this
            return new Promise((resolve, reject) => {
                http.post(`/api/students/sessions/${this.sessionId}/submit/`, {
                    responses: this.currentResponses,
                })
                    .then(function (response) {
                        // Set the saved responses on the session.
                        vm.session.panel_responses = response.data.panel_responses
                        vm.responsesReceivedCb()
                        resolve()
                    })
                    .finally(function () {
                        vm.submitIsLoading = false
                        reject()
                    })
            })
        },
        responsesReceivedCb() {
            let vm = this
            // Trigger a data refresh in all discovery-type panels.
            this.currentPanelsFlat
                .filter((p) => p.panel_type === "discovery")
                .forEach((panel) => {
                    vm.$refs["panel-" + panel.id][0].refreshData()
                })
            this.updateSessionScore()
        },
        updateSessionScore() {
            // If live scoring is enabled, fetch the updated score for the session.
            if (!this.sessionCase.show_live_score) {
                return
            }
            let vm = this
            http.get(`/api/sessions/${this.session.id}/score/`).then(function (response) {
                vm.$store.commit("setSessionScore", response.data.score)
                if (response.data.min_score_triggered) {
                    // Minimum score branching has been triggered, so refresh the session.
                    vm.fetchSession()
                }
            })
        },
        triggerVitals(payload) {
            let vm = this
            this.currentPanelsFlat
                .filter((p) => p.panel_type == "vitals")
                .forEach((panel) => {
                    vm.$refs["panel-" + panel.id][0].activateTriggers(payload)
                })
        },
        // This method is called by parent components when timers run out.
        async timerExpired(timerId) {
            this.$store.dispatch("addLoad")
            let vm = this
            // Find voice recording panels that are unsubmitted, so that we can save what
            // the learner has recorded so far.
            let voiceRecordingPanelsNotSubmitted = vm.currentPanelsFlat.filter(
                (p) => p.panel_type == "voice_recording" && !p.isSubmitted
            )
            if (vm.submitIsRequired || voiceRecordingPanelsNotSubmitted.length) {
                // Stop active voice recordings.
                if (voiceRecordingPanelsNotSubmitted.length) {
                    voiceRecordingPanelsNotSubmitted.forEach(async (panel) => {
                        vm.$refs["panel-" + panel.id][0].stopRecording()
                    })
                    // This delay is a hack used to solve for the fact that MediaRecorders take
                    // some time to stop. This ensures that voice recording data is available to
                    // be submitted to the server. There's probably a better way to do this using
                    // Promises.
                    await Utilities.timeDelay(800)
                }
                // Wait for responses to submit.
                await vm.submitResponses()
            }
            http.post(`/api/students/timers/${timerId}/timeout/`)
                .then(function (response) {
                    // Only replace the session if the node has changed.
                    if (response.data.current_node.id !== vm.session.current_node.id) {
                        vm.session = response.data
                        vm.updateSession(response.data)
                    }
                })
                .finally(() => {
                    vm.$store.dispatch("removeLoad")
                })
        },
        handleEditAction(panel, action) {
            if (action === "edit") {
                this.openEditPanel(panel)
            } else if (action === "clone") {
                this.openClonePanel(panel)
            } else if (action === "delete") {
                this.openDeletePanel(panel)
            }
        },
        openEditCurrentFullscreen() {
            if (this.fullScreenPanels.length) {
                this.openEditPanel(this.fullScreenPanels[0])
            }
        },
        openEditPanel(panel) {
            this.$refs.panelEditor.open(panel)
        },
        openClonePanel(panel) {
            this.$refs.panelEditor.openClone(panel)
        },
        openDeletePanel(panel) {
            this.$refs.panelEditor.openDelete(panel)
        },
        // Toggle the vertical flex setting of a panel within a column.
        togglePanelVerticalFlex(panel, column) {
            // Update the panel and save the change.
            let newFlex = panel.vertical_flex_display === "grow" ? "shrink" : "grow"
            panel.vertical_flex_display = newFlex
            // Store the change to the panel in the db.
            this.updatePanelFields(panel, { vertical_flex_display: newFlex })
            // Make sure that at least one panel in the column is set to flex-grow.
            let noExistingGrowingPanel = column.panels.find((p) => p.vertical_flex_display === "grow") === undefined
            if (noExistingGrowingPanel) {
                let adjacentPanel = column.panels.filter((p) => p.id !== panel.id)[0]
                adjacentPanel.vertical_flex_display = "grow"
                // Store the change to the panel in the db.
                this.updatePanelFields(adjacentPanel, { vertical_flex_display: "grow" })
            }
        },
        // Toggle the vertical content alignment setting of a panel.
        togglePanelVerticalContentAlign(panel) {
            let newAlign = panel.vertical_align === "center" ? "top" : "center"
            panel.vertical_align = newAlign
            // Store the change to the panel in the db.
            this.updatePanelFields(panel, { vertical_align: newAlign })
        },
        // Update a set of fields for a panel.
        updatePanelFields(panel, fieldsObject) {
            if (!this.editMode) {
                // Prevent authoring actions when edit mode isn't active.
                return
            }
            let payload = {}
            for (const field in fieldsObject) {
                payload[field] = fieldsObject[field]
            }
            // Do this in update silently in the background, don't show a
            // success notification.
            http.put(`/api/panels/${panel.id}/basic/`, payload)
        },
        createPanel(order = null) {
            this.$emit("create-panel", order)
        },
        initWindowTitle() {
            let title = this.sessionCase.public_title
            if (this.isCommunitySession) {
                title = this.sessionCase.community_title
            }
            document.title = `Viewing: ${title}`
        },
        // Handle user choosing to resume their session. Simply remove the resume flag from
        // the URL's query parameters.
        resumeSession() {
            // This is a hack - replace the route with the current path to remove the query
            // parameters.
            // TODO: Find a solution that only removes the 'resume' flag.
            this.$router.replace(this.$route.path)
        },
        // Skip any full screen videos to the end.
        skipVideos() {
            let vm = this
            let panelTypes = [
                "multichoice",
                "multiselect",
                "singleselect",
                "step_order",
                "tabs",
                "textarea",
                "custom",
                "custom_form",
                "voice_recording",
                "notepad",
                "categories",
                "3d_video",
            ]
            this.currentPanelsFlat
                .filter((p) => panelTypes.includes(p.panel_type))
                .forEach((panel) => {
                    vm.$refs["panel-" + panel.id][0].skipVideos()
                })
        },
        // Called after screen layout has been updated. Refresh any panel that needs to be re-rendered when
        // the width of its containing block changes.
        refreshPanelDisplay() {
            let vm = this
            this.currentPanelsFlat
                .filter((p) => ["image_hotspots", "3d_video", "categories", "custom"].includes(p.panel_type))
                .forEach((p) => {
                    let panel = vm.$refs["panel-" + p.id][0]
                    if (panel) {
                        panel.reset()
                    }
                })
        },
        // Called when a panel is chosen (i.e. by clicking and holding) to be dragged.
        choosePanelForDrag(event) {
            // Get a reference to the panel object being dragged using its ID.
            let panelID = event.item.getAttribute("data-panel-id")
            let panel = this.currentPanelsFlat.find((p) => p.id == panelID)
            this.$set(panel, "isChosenForDrag", true)

            // Store the width of the chosen panel so we can preserve its width while being
            // dragged. For some reason, the drag 'ghost' sometimes doesn't reflect the width of
            // the original (dragged) element.
            this.$set(panel, "tempDragWidth", event.target.offsetWidth)
        },
        // Called when a panel is un-chosen (i.e. let go by the user) for dragging.
        unchoosePanelForDrag(event) {
            // Get a reference to the panel object being dragged using its ID.
            let panelID = event.item.getAttribute("data-panel-id")
            let panel = this.currentPanelsFlat.find((p) => p.id == panelID)
            if (panel) {
                panel.isChosenForDrag = false
            }
        },
        // Called when a panel drag action begins.
        startPanelDrag(column) {
            this.isDragging = true
            // Save the width of the column that the panel was dragged from, so that the dragged content
            // doesn't get squished or blow up in size. Don't let the width exceed the remaining width
            // available on the screen.
            this.createColumnWidth = Math.min(column.width, this.availableColumnWidth)
            this.createColumnWidth = Math.max(this.createColumnWidth, minColumnWidth)
            this.$store.commit("setNodeLayoutWasEdited", true)
        },
        // Called when a panel drag action ends.
        endPanelDrag() {
            this.isDragging = false
        },
        // Called when a panel is dropped into the left or right create-column drop areas. Create
        // a new column and place the dropped panel into it.
        dropToCreateColumn(position = "left") {
            // Get the panel that was dropped.
            let droppedPanel = this.createColumnDroppedPanels[0]
            droppedPanel.isChosenForDrag = false
            // Create a new column for the panel to be placed in.
            let newColumn = {
                width: this.createColumnWidth,
                panels: [droppedPanel],
                uid: Utilities.getRandomIdStr(),
            }
            // Append the column to the start or end depending on if the panel was dragged to the
            // left or right side drop area.
            if (position === "left") {
                this.session.current_node.columns.unshift(newColumn)
            } else {
                this.session.current_node.columns.push(newColumn)
            }
            // Clear the dropped panel list.
            this.createColumnDroppedPanels = []
            // Save the new layout.
            this.savePanelLayout()
        },
        // Save the current layout of panels in the db.
        savePanelLayout(columns = null) {
            this.storedNodeNeedsUpdate()
            if (columns === null) {
                columns = this.session.current_node.columns
            }
            let panelLayout = columns.map((c) => {
                return {
                    width: c.width,
                    panel_ids: c.panels.map((p) => p.id),
                }
            })
            return http.put(`/api/nodes/${this.session.current_node.id}/layout/`, panelLayout)
        },
        // Called after the panel layout changes. Remove any empty panel columns that are
        // present and store the new layout in the db.
        layoutChangedCb($event) {
            // When panels are added to columns, make sure they're set to flex-grow vertically.
            if ($event.added) {
                let panel = $event.added.element
                panel.vertical_flex_display = "grow"
                this.updatePanelFields(panel, { vertical_flex_display: "grow" })
            }
            // Loop through and remove any empty columns, and make sure that at least one panel
            // in the column is set to flex-grow.
            let columns = this.session.current_node.columns
            for (let i = 0; i < columns.length; i++) {
                if (columns[i].panels.length === 0) {
                    // Remove the empty column.
                    columns.splice(i, 1)
                } else {
                    let noExistingGrowingPanels =
                        columns[i].panels.find((p) => p.vertical_flex_display === "grow") === undefined
                    if (noExistingGrowingPanels) {
                        let adjacentPanel = this.session.current_node.columns[i].panels[0]
                        adjacentPanel.vertical_flex_display = "grow"
                        this.updatePanelFields(adjacentPanel, { vertical_flex_display: "grow" })
                    }
                }
            }
            this.savePanelLayout()
            this.initEditBtnPopovers()
            // Panel sizing may have changed, so refresh panels to make sure they're still
            // displayed properly.
            let vm = this
            this.$nextTick(() => {
                vm.refreshPanelDisplay()
            })
        },
        // Open the create panel modal. Save the column and position to insert it once created.
        openCreatePanelAtPosition(columnIndex, position = "end") {
            this.createAtColumnIndex = columnIndex
            this.createAtPosition = position
            this.$refs.panelCreator.open()
        },
        // Called after a new panel has been created - inserts the new panel at the
        // position the user originally clicked.
        panelCreatedCb(newPanel) {
            // Create an editable copy of the columns array. Simplify column objects before
            // creating the - this fixes a bug where a cross origin error is thrown when vimeo
            // embeds are present on the screen.
            let existingColumns = this.session.current_node.columns.map((c) => {
                return {
                    ...c,
                    panels: c.panels.map((p) => {
                        return {
                            id: p.id,
                        }
                    }),
                }
            })
            let columns = JSON.parse(JSON.stringify(existingColumns))

            // If the user is creating the first panel for the node, make it slightly larger
            // than when creating subsequent panels.
            let newPanelWidth = this.currentNodeHasColumns ? 450 : 650

            if (this.createAtColumnIndex === -1) {
                columns.unshift({
                    width: newPanelWidth,
                    panels: [newPanel],
                    uid: Utilities.getRandomFiveDigitStr(),
                })
            } else if (columns[this.createAtColumnIndex] === undefined) {
                // Create a new column containing the new panel.
                columns.push({
                    width: newPanelWidth,
                    panels: [newPanel],
                    uid: Utilities.getRandomFiveDigitStr(),
                })
            } else if (this.createAtPosition === "start") {
                // Add to the top of the existing column.
                columns[this.createAtColumnIndex].panels.unshift(newPanel)
            } else {
                // Add to the bottom of the existing column.
                columns[this.createAtColumnIndex].panels.push(newPanel)
            }

            let vm = this
            this.savePanelLayout(columns).then(async () => {
                // Refresh the session - this will ensure that all metadata is up to date
                // (e.g. number of required correct responses).
                vm.refreshSession().then(() => {
                    // Reset the create-placeholder to collapse it - the newly created panel
                    // will take its place.
                    vm.createAtColumnIndex = null
                })
                // Open the edit modal.
                vm.openEditPanel(newPanel)
            })
        },
        // Called when the create panel dialog is closed/dismissed.
        panelCreateCancelled() {
            this.createAtColumnIndex = null
        },
        // Called after a panel has been updated.
        panelUpdatedCb() {
            this.refreshSession()
            this.storedNodeNeedsUpdate()
        },
        // Called after a panel has been removed.
        panelDeletedCb() {
            this.storedNodeNeedsUpdate()
            this.refreshSession()
        },
        // Close all panel edit modals.
        closeEditModals() {
            this.$refs.panelEditor.closeEditModals()
        },
        // Clear the locally stored node to make sure it gets refreshed next time it's retrieved.
        storedNodeNeedsUpdate() {
            this.$store.commit("selectedNodeRequiresUpdate")
        },
        // Initialize panel column drag-to-resize.
        initColumnResize() {
            let vm = this
            this.resizableColumnInteract = interact(".panel-column").resizable({
                // Resize from handles only.
                edges: { left: ".resize-handle-container-left", right: ".resize-handle-container-right" },
                margin: 1, // Controls the margin for drag handles.
                listeners: {
                    move(event) {
                        let newWidth = event.rect.width
                        if (newWidth > vm.maxWidthForResizingColumn) {
                            // Don't let the new width exceed the maximum.
                            newWidth = vm.maxWidthForResizingColumn
                        }
                        // Ensure the new width conforms to the grid snapping size. This is set in the interact
                        // options, but without this check, widths sometimes end up with decimals.
                        newWidth = Math.round(newWidth / panelGridSnappingSize) * panelGridSnappingSize
                        let target = event.target
                        target.style.width = newWidth + "px"
                        vm.activeResizeWidth = newWidth
                    },
                    start(event) {
                        let resizingColumnIndex = Number(event.target.getAttribute("data-col-index"))
                        // Store the index column that's being resized.
                        vm.resizingColumnIndex = resizingColumnIndex
                        vm.isResizingColumn = true
                        // Store the size of the column being resized so we can display it to the user.
                        vm.activeResizeWidth = vm.session.current_node.columns[resizingColumnIndex].width
                        vm.$store.commit("setLayoutResizeIsActive", true)
                    },
                    end(event) {
                        let newWidth = event.rect.width
                        if (newWidth > vm.maxWidthForResizingColumn) {
                            // Don't let the new width exceed the maximum.
                            newWidth = vm.maxWidthForResizingColumn
                        }
                        vm.resizingColumnIndex = null
                        vm.isResizingColumn = false
                        // Fetch the index of the column being resized.
                        let resizingColumnIndex = Number(event.target.getAttribute("data-col-index"))
                        // Update the width of the resized column.
                        vm.session.current_node.columns[resizingColumnIndex].width = newWidth
                        // Save the panel layout and refresh panel display on the next render.
                        vm.savePanelLayout()
                        vm.$nextTick(() => {
                            vm.refreshPanelDisplay()
                        })
                        vm.$store.commit("setLayoutResizeIsActive", false)
                    },
                },
                modifiers: [
                    interact.modifiers.snapSize({
                        targets: [
                            { width: panelGridSnappingSize },
                            interact.snappers.grid({ width: panelGridSnappingSize, height: panelGridSnappingSize }),
                        ],
                    }),
                    // Defines the min and maximum size of columns. Maximum width is also enforced dynamically
                    // in the 'move' and 'end' listeners, to allow us to set it dynamically based on the available
                    // space on the screen.
                    interact.modifiers.restrictSize({
                        min: { width: minColumnWidth },
                        max: { width: maxTotalColumnWidth },
                    }),
                ],
            })
        },
        // Initialize popovers on all toolbar buttons.
        initEditBtnPopovers() {
            this.$nextTick(() => {
                let config = {
                    boundary: "window",
                    customClass: "shadow-sm toolbar-popover-text",
                    trigger: "hover",
                    delay: { show: 400, hide: 0 },
                    placement: "top",
                }
                $(".toolbar-popover").popover(config)
            })
        },
    },
    watch: {
        currentNode: function (val) {
            this.animationsComplete = false

            if (this.sessionCase.navigation_enabled) {
                this.updateNavigationNodeList()
            }

            // Propagate the new node up to the parent.
            this.$emit("nodeChange", val)

            this.initializeCustomPanels()

            if (this.sessionCase.preload_files) {
                this.prefetchElements()
            }
            if (this.sessionCase.preload_nodes) {
                this.prefetchNodes()
            }

            // Hack fix - setting these values in the datastore here because sometimes
            // the watchers don't pick up the changes in these computed properties.
            this.$store.commit("setVideoIsPlaying", this.videoIsPlaying)
            this.$store.commit("setContinueIsEnabled", this.continueBtnEnabled)
            this.$store.commit("setSubmitIsEnabled", this.submitIsEnabled)
            this.$store.commit("setShowSubmitBtn", this.showSubmitBtn)
            this.$store.commit("setSubmitBtnLabel", this.submitButtonLabel)
            this.$store.commit("setSubmitBtnDisabledExplanation", this.submitBtnDisabledExplanation)
            this.$store.commit("setContinueBtnDisabledExplanation", this.continueBtnDisabledExplanation)

            if (this.editMode && !this.readOnly) {
                this.initColumnResize()
            }
            this.updateSessionScore()

            if (this.editMode) {
                this.initEditBtnPopovers()
                // This is a hack to fix hotspots being positioned incorrectly when edit mode is turned on.
                // The issue seems to be caused the by left & right hand side 'create' bars/buttons, which
                // can squish the content on the screen (depending on the device screen size).
                let vm = this
                window.setTimeout(() => {
                    vm.refreshPanelDisplay()
                }, 50)
            }

            // Make sure image edit mode is not active.
            this.$store.commit("setImageEditModeIsActive", false)

            // Make sure animations don't play twice.
            let vm = this
            window.setTimeout(() => {
                vm.animationsComplete = true
            }, 1000)

            // Experimental - this is to control the visibility of the 'Complete & Continue'
            // button when Affinity is iframed into Thinkific. Requires custom javascript
            // to be present within 'site footer code' in Thinkific to receive the message.
            if (window !== window.top) {
                if (val.completion_node || this.session.complete) {
                    window.parent.postMessage({ type: "showCompleteAndContinue" }, "*")
                } else {
                    window.parent.postMessage({ type: "hideCompleteAndContinue" }, "*")
                }
            }
        },
        activeTimers: function (val) {
            this.$store.commit("setSessionTimers", val)
        },
        videoIsPlaying: function (val) {
            this.$store.commit("setVideoIsPlaying", val)
        },
        fullScreenIsActive: function (val) {
            this.$store.commit("setFullScreenIsActive", val)
        },
        continueIsLoading: function (val) {
            this.$store.commit("setContinueIsLoading", val)
        },
        submitIsLoading: function (val) {
            this.$store.commit("setSubmitIsLoading", val)
        },
        continueBtnEnabled: function (val) {
            this.$store.commit("setContinueIsEnabled", val)
        },
        continueBtnDisabledExplanation: function (val) {
            this.$store.commit("setContinueBtnDisabledExplanation", val)
        },
        submitBtnDisabledExplanation: function (val) {
            this.$store.commit("setSubmitBtnDisabledExplanation", val)
        },
        submitIsEnabled: function (val) {
            this.$store.commit("setSubmitIsEnabled", val)
        },
        showContinueBtn: function (val) {
            this.$store.commit("setShowContinueBtn", val)
        },
        showSubmitBtn: function (val) {
            this.$store.commit("setShowSubmitBtn", val)
        },
        submitButtonLabel: function (val) {
            this.$store.commit("setSubmitBtnLabel", val)
        },
        submitIsAnalyzing: function (val) {
            this.$store.commit("setSubmitIsAnalyzing", val)
        },
        editMode: function (val) {
            if (val && this.resizableColumnInteract) {
                // Enable column resize.
                this.resizableColumnInteract.resizable(true)
            } else if (!val && this.resizableColumnInteract) {
                // Disable column resize.
                this.resizableColumnInteract.resizable(false)
            } else if (val) {
                // Initialize (and enable) column resize.
                this.initColumnResize()
            }
            // Turning off edit mode removes extra margins and padding, which may affect the sizing of
            // displayed panels. Refresh panels to make sure they're displayed properly.
            this.refreshPanelDisplay()
        },
    },
    // When the page is loaded for the first time, continue the session.
    async created() {
        // This check fixes an issue in Safari where cookies are blocked within
        // iframes until explicit steps are followed by the user.
        let hasLocalStorageAccess = await Utilities.checkIframeLocalStorageAccess()
        if (!hasLocalStorageAccess) {
            // Show the user a modal with the steps required to give permissions.
            this.$refs.browserStoragePermissionsModal.show()
            return
        }

        // Fetch data for the session.
        this.$store.dispatch("addLoad")
        let vm = this
        this.continueSession()
            .then(() => {
                vm.$emit("update:sessionInitialized", true)
            })
            .catch((error) => {
                if (error.response.status == 404) {
                    vm.$emit("session-not-found")
                }
            })
            .finally(() => {
                vm.$store.dispatch("removeLoad")
            })
    },
    mounted() {
        if (this.$route.query.resume) {
            $("#resume-restart-modal").modal("show")
        }
    },
    beforeDestroy() {
        // Destroy all toolbar popovers.
        $(".toolbar-popover").popover("dispose")
    },
    destroyed() {
        this.$store.dispatch("clearSession")
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/resize.scss";
@import "@/assets/styles/paneltoolbar.scss";

$column-bg: #d2ddeb;
$column-bg-hover: darken($column-bg, 3%);
$node-view-max-width: 1800px;
$vertical-stacked-panel-width: 700px;
$create-column-hover-area-y: 25px;
$create-column-hover-area-x: 50px;
$panel-gutter-width: 13px;
$panel-column-padding: 8px;

.case-session {
    margin-top: 30px;
    margin-bottom: 20px;
    @media (min-width: $breakpoint-sm) {
        margin-bottom: 60px;
        margin-top: 54px;
    }
    @media (min-height: 1200px) {
        margin-bottom: 90px !important;
    }
}

.case-session-edit-mode {
    margin-top: 10px;
    margin-bottom: 20px;
    @media (min-width: $breakpoint-sm) {
        margin-bottom: 60px;
        margin-top: 30px;
    }
    @media (min-height: 1200px) {
        margin-bottom: 90px !important;
    }
}

.node-holder {
    @media (max-width: ($breakpoint-session - 1)) {
        -webkit-box-align: center !important;
        -ms-flex-align: center !important;
        align-items: center !important;
    }
}

.node-container {
    max-width: $node-view-max-width;
    padding-left: 0px;
    padding-right: 0px;
}

.prefetch-element-container {
    opacity: 0;
    height: 1px;
    width: 1px;
    position: absolute;
}

.node-history-dropdown {
    max-height: 60vh;
    @media (min-width: $breakpoint-md) {
        max-height: 50vh;
    }
    overflow-y: scroll;
}

.node-history-item {
    max-width: 240px;
    @media (min-width: $breakpoint-md) {
        max-width: 300px;
    }
    text-overflow: ellipsis;
    overflow-x: hidden;
}

.dropdown-toggle::after {
    display: none;
}

.dropup-icon {
    font-size: 0.85rem;
    margin-bottom: 2px;
    margin-left: 1px;
}

.btn-session {
    font-weight: 400;
}

.btn-session-bottom {
    &:focus {
        box-shadow: 0 0 0 2px $primary;
    }
}

.empty-screen-display {
    height: 300px;
    width: 360px;
    color: $gray-600;
    background-color: $column-bg;
    margin: 40px;
    &:hover {
        background-color: $column-bg-hover;
        color: $gray-700;
    }
    cursor: pointer;
    transition: all ease $edit-action-transition-time;
}

.panel-column-editable {
    transition: background-color $resize-transition-time, padding $resize-transition-time;
    border-radius: $border-radius-lg;
    background-color: $column-bg;
    margin-top: 0px;
    margin-bottom: 0px;
    @media (max-width: ($breakpoint-session - 1)) {
        margin-top: 8px;
        margin-bottom: 8px;
    }
}

.panel-column {
    padding: $panel-column-padding;
    @media (min-width: $breakpoint-sm) {
        margin-left: $panel-gutter-width - $panel-column-padding;
        margin-right: $panel-gutter-width - $panel-column-padding;
    }
    @media (max-width: ($breakpoint-session - 1)) {
        width: $vertical-stacked-panel-width !important;
        max-width: 100%;
    }
}

.panel-column-smooth {
    transition: all $resize-transition-time !important;
}

.panel-column-visible {
    opacity: 1;
}

.panel-column-drag-active {
    background-color: $column-bg;
}

.panel-column-create {
    min-width: 100px;
}

.panel-column-hide {
    opacity: 0;
}

.panel-drag-anchor {
    cursor: move;
    pointer-events: none;
    .icon {
        margin-bottom: 1px;
    }
}

.column-width-popover {
    color: $light;
    position: absolute;
    z-index: 10;
    top: -5px;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity 0.1s linear;
    font-weight: 500;
    font-size: 15px;
    padding-left: 8px;
    padding-right: 8px;
    padding-top: 5px;
    padding-bottom: 5px;
}

.panel {
    transition: all $resize-transition-time;
    margin-top: $panel-gutter-width;
    margin-bottom: $panel-gutter-width;
    &:hover {
        .panel-toolbar {
            opacity: 1;
        }
    }
}

.panel-draggable {
    cursor: move;
}

.add-panel-inline {
    margin-top: 4px;
    margin-bottom: 4px;
    background-color: transparent;
    height: 35px;
    border-radius: $border-radius-lg;
    cursor: pointer;
    &:active {
        background-color: $column-bg;
    }
    transition: all $edit-action-transition-time ease 0.015s;
    .icon {
        color: transparent;
        transition: all $edit-action-transition-time ease 0.015s;
        margin-top: 1px;
    }
}

.add-panel-inline-show {
    background-color: $column-bg;
    .icon {
        color: $gray-600;
    }
}

.panel-column-content {
    &:hover {
        .add-panel-inline:not(.add-panel-inline-hide) {
            background-color: $column-bg;
            .icon {
                color: $gray-600;
            }
            &:hover {
                background-color: $column-bg-hover;
            }
        }
    }
}

.create-column-container {
    padding-left: $create-column-hover-area-x;
    padding-right: $create-column-hover-area-x;
    margin-right: -$create-column-hover-area-x;
    margin-left: -$create-column-hover-area-x;
    padding-top: $create-column-hover-area-y;
    padding-bottom: $create-column-hover-area-y;
    margin-bottom: -$create-column-hover-area-y;
    margin-top: -$create-column-hover-area-y;
    &:hover {
        .create-column-enabled {
            background-color: $column-bg;
            .icon {
                color: $gray-600;
            }
            &:hover {
                background-color: $column-bg-hover;
            }
        }
    }
}

.create-column {
    margin-top: 60px;
    margin-bottom: 60px;
    margin-left: 6px;
    margin-right: 6px;
    background-color: transparent;
    width: $vertical-stacked-panel-width;
    max-width: 100%;
    border-radius: $border-radius-lg;
    cursor: pointer;
    transition: all $edit-action-transition-time;
    .icon {
        transition: all $edit-action-transition-time;
        color: transparent;
    }
    @media (max-width: ($breakpoint-session - 1)) {
        margin-top: 8px;
        margin-bottom: 8px;
        height: 30px;
    }
    @media (min-width: $breakpoint-session) {
        width: 30px;
    }
}

.create-column-show {
    background-color: $column-bg-hover;
    .icon {
        color: $gray-600;
    }
}

.ml-tiny {
    margin-left: 2px;
}

.cursor-resize {
    cursor: ew-resize !important;
}

.mt-bottom-navs {
    margin-top: 36px;
}

.gelatine {
    animation: gelatine 0.5s 1;
}

@keyframes gelatine {
    from,
    to {
        transform: scale(1, 1);
    }
    25% {
        transform: scale(0.9, 1.1);
    }
    //   50% { transform: scale(1.1, 0.9); }
    //   75% { transform: scale(0.95, 1.05); }
}

.panel-settle-animate {
    animation-fill-mode: backwards;
    animation-name: settle;
    animation-direction: forwards;
    animation-timing-function: ease-out;
    animation-duration: 0.25s;
}

@keyframes settle {
    0% {
        transform: translateY(6px);
        opacity: 0.85;
    }
    99% {
        transform: translateY(0);
        opacity: 1;
    }
    100% {
        transform: unset;
        opacity: 1;
    }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables.scss";

.rich-text-content {
    p {
        min-height: 1.5rem;
        word-break: break-word;
    }

    table {
        th {
            background-color: $offwhite;
            padding: 8px !important;
            vertical-align: middle !important;
        }
        td {
            padding: 8px !important;
            word-wrap: break-word;
            vertical-align: middle !important;
        }
    }
}

.toolbar-popover-text {
    font-size: 82% !important;
}

.panel {
    .card {
        border: none;
        border-radius: $border-radius-lg;
    }
    .card-header {
        border-bottom: none;
    }
    .card-footer {
        border-top: none;
    }
}
</style>
